import { Link } from '@tanstack/react-router';
import { type FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DEMO_PERSONAS, type DemoPersona, demoLogin } from '@shkola/api';
import { useAuth } from '../auth/AuthProvider';
import { readEnv } from '../env';
import { Button, Card, CenteredScreen, ErrorText, Field } from '../components/ui';
import { BrandWord } from '../design/brand';

export function LoginPage() {
  const { t } = useTranslation();
  const { supabase } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();

  // never in production: the Edge Function does not exist there
  const showDemo =
    readEnv(import.meta.env as Record<string, string | undefined>).appEnv !== 'production';

  async function asPersona(persona: DemoPersona) {
    setBusy(true);
    setError(undefined);
    try {
      await demoLogin(supabase, persona);
    } catch {
      setError(t('common.error'));
    } finally {
      setBusy(false);
    }
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(undefined);
    const { error: signInError } = await supabase.auth.signInWithPassword({ email, password });
    setBusy(false);
    if (signInError) {
      // never reveal whether the email exists
      setError(
        signInError.status === 429 ? t('auth.tooManyAttempts') : t('auth.invalidCredentials'),
      );
    }
  }

  return (
    <CenteredScreen>
      <Card>
        <h1 className="mb-3 text-ink" aria-label={t('common.appName')}>
          <BrandWord height={24} />
        </h1>
        <p className="mt-1 mb-7 text-[15px] text-muted">{t('auth.signInSubtitle')}</p>
        <form className="space-y-4" onSubmit={onSubmit}>
          <Field
            id="email"
            label={t('auth.email')}
            type="email"
            autoComplete="username"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <Field
            id="password"
            label={t('auth.password')}
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <ErrorText>{error}</ErrorText>
          <Button type="submit" disabled={busy}>
            {busy ? t('auth.signingIn') : t('auth.signIn')}
          </Button>
        </form>
        <p className="mt-6 text-center text-sm">
          <Link
            to="/invite"
            className="font-medium text-ink underline decoration-[#E4E0D7] decoration-2 underline-offset-4 hover:decoration-current"
          >
            {t('auth.haveInvite')}
          </Link>
        </p>
        {showDemo && (
          <div className="mt-6 border-t border-[#E4E0D7] pt-5">
            <p className="mb-3 text-center text-[13px] font-medium text-muted">
              {t('auth.demoTitle')}
            </p>
            <div className="grid grid-cols-2 gap-2">
              {DEMO_PERSONAS.map((persona) => (
                <button
                  key={persona}
                  type="button"
                  disabled={busy}
                  onClick={() => void asPersona(persona)}
                  className="h-10 rounded-full bg-white px-3 text-sm font-medium text-ink ring-1 ring-[#E4E0D7] ring-inset transition-colors hover:bg-[#FAF9F6] disabled:opacity-50"
                >
                  {t(`auth.demoPersonas.${persona}`)}
                </button>
              ))}
            </div>
          </div>
        )}
      </Card>
    </CenteredScreen>
  );
}
